/**
 * SUNTRONIX ONE — Platform Foundation hub.
 * Shows SCIM, Passkeys, Events, Contracts, Sandbox, Developer Portal
 * and Webhook Replay live from `/api/platform/foundation/health`.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, Key, Radio, FileCode2, Beaker, Cable, RotateCw, ShieldCheck,
} from "lucide-react";

const CARDS = [
  { k: "scim_users_active",  label: "SCIM active users",   tone: "blue",  icon: Users },
  { k: "passkeys",            label: "Passkeys registered", tone: "mint",  icon: Key },
  { k: "events_delivered",   label: "Events delivered",    tone: "mint",  icon: Radio },
  { k: "events_failed",      label: "Events failed",       tone: "coral", icon: Radio },
  { k: "contracts",           label: "API contracts",       tone: "blue",  icon: FileCode2 },
  { k: "breaking_pending",   label: "Breaking pending",    tone: "coral", icon: ShieldCheck },
  { k: "sandbox_runs",       label: "Sandbox runs",        tone: "amber", icon: Beaker },
  { k: "oauth_clients",      label: "OAuth clients",       tone: "blue",  icon: Cable },
  { k: "webhook_retrying",   label: "Webhook retrying",    tone: "amber", icon: RotateCw },
];
const TONE = {
  blue:  { bg: "#EFF5FF", accent: "#246BFD" },
  mint:  { bg: "#ECFDF6", accent: "#169C70" },
  amber: { bg: "#FFF8E7", accent: "#B25A00" },
  coral: { bg: "#FFF0F2", accent: "#C2333F" },
};

export default function PlatformFoundation() {
  const [health, setHealth] = useState(null);
  const [events, setEvents] = useState([]);

  const load = async () => {
    try {
      const [h, e] = await Promise.all([
        api.get("/platform/foundation/health"),
        api.get("/platform/events?limit=20"),
      ]);
      setHealth(h.data); setEvents(e.data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const simulate = async (scenario) => {
    try {
      const { data } = await api.post("/platform/sandbox/simulate",
        { connector_id: "microsoft-intune", scenario });
      toast.success(`Sandbox · ${scenario} → HTTP ${data.result.http_status}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-[1400px] space-y-4" data-testid="platform-foundation">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-[#667085]">
          Administration · Platform
        </div>
        <h1 className="text-2xl font-heading font-semibold text-[#101828] tracking-tight">
          Platform foundation
        </h1>
        <p className="text-[13px] text-[#667085] mt-1 max-w-3xl">
          Identity → Connect → Event → Reconcile → Workflow → Observe → Verify → Audit.
          One identity, one event model, one contract registry.
        </p>
      </div>

      {health && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-2">
          {CARDS.map((c) => {
            const t = TONE[c.tone];
            const Icon = c.icon;
            return (
              <div key={c.k}
                   className="rounded-md border border-slate-200 p-3 relative overflow-hidden"
                   style={{ background: `linear-gradient(180deg, ${t.bg} 0%, #FFFFFF 60%)` }}
                   data-testid={`pf-${c.k}`}>
                <div className="absolute inset-y-0 left-0 w-1" style={{ background: t.accent }} />
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">{c.label}</div>
                  <Icon className="h-3.5 w-3.5" style={{ color: t.accent }} />
                </div>
                <div className="mt-1 text-xl font-semibold text-[#101828]">
                  {health[c.k] ?? 0}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sandbox */}
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="text-[11px] uppercase tracking-[0.14em] text-[#667085] mb-2">
          Sandbox Lab — provider simulator
        </div>
        <div className="text-[12px] text-[#667085] mb-3">
          Deterministic scenarios · no production transaction is ever created from Sandbox.
        </div>
        <div className="flex flex-wrap gap-2">
          {["success", "failure", "timeout", "rate_limit", "expired_token",
             "duplicate_event", "invalid_payload", "partial_response"].map((s) => (
            <button key={s} onClick={() => simulate(s)}
                    data-testid={`pf-sandbox-${s}`}
                    className="h-8 px-3 rounded-md border border-slate-200 text-[12px]
                                text-[#101828] hover:border-brand-blue hover:text-brand-blue transition-colors">
              {s.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </div>

      {/* Recent events */}
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="text-[11px] uppercase tracking-[0.14em] text-[#667085] mb-2">
          ONE Event Bus · recent
        </div>
        {events.length === 0 ? (
          <div className="text-[12px] text-[#98A2B3] py-4 text-center">
            No events yet. Every asset / order / support / connector action will
            emit an idempotent event here.
          </div>
        ) : (
          <ul className="space-y-1 text-[12px]">
            {events.map((e) => (
              <li key={e.id} className="grid grid-cols-[180px_140px_1fr] gap-3"
                  data-testid={`pf-event-${e.id}`}>
                <span className="text-[#667085]">{e.timestamp?.slice(0, 19).replace("T", " ")}</span>
                <span className="font-mono text-[#101828]">{e.event_type}</span>
                <span className="text-[#667085] truncate">
                  {e.entity_type} · {(e.entity_id || "").slice(0, 24)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
